import { fetchWithTimeout, createNetworkError } from './http';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const SESSION_RESTORED_EVENT = 'auth:session-restored';
export const DRAFT_STORAGE_PREFIX = 'draft:v1:';
export const ACCOUNT_NOTICE_KEY = 'auth:notice';
const USER_INACTIVE_CODE = 'USER_INACTIVE';
const DEFAULT_INACTIVE_MESSAGE = 'Ваша учётная запись деактивирована. Обратитесь к администратору.';

const TOKEN_ISSUED_AT_KEY = 'tokenIssuedAt';
const REFRESH_WHEN_REMAINING_RATIO = 1 / 3;
const REFRESH_RETRY_COOLDOWN_MS = 60 * 1000;
const KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;

// Активная сессия этой вкладки: отличает "токен истёк, пока открыта форма" (модальное окно входа)
// от "открыли приложение с протухшим токеном" (обычный переход на /login).
let sessionActive = false;
let sessionExpired = false;
let refreshPromise = null;
let lastRefreshAttemptAt = 0;

export const getToken = () => localStorage.getItem('token');

export const decodeJwtPayload = (token) => {
    try {
        const segment = String(token).split('.')[1];
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
};

const isTokenValid = (token) => {
    const payload = token ? decodeJwtPayload(token) : null;
    return Boolean(payload && payload.exp > Date.now() / 1000);
};

const storeToken = (token) => {
    localStorage.setItem('token', token);
    localStorage.setItem(TOKEN_ISSUED_AT_KEY, String(Math.floor(Date.now() / 1000)));
};

const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedBranchId');
    localStorage.removeItem(TOKEN_ISSUED_AT_KEY);
    sessionActive = false;
    sessionExpired = false;
    lastRefreshAttemptAt = 0;
};

const clearDrafts = () => {
    try {
        Object.keys(localStorage)
            .filter((key) => key.startsWith(DRAFT_STORAGE_PREFIX))
            .forEach((key) => localStorage.removeItem(key));
    } catch {
        // Хранилище недоступно: черновиков там тоже нет
    }
};

export const isAuthenticated = () => {
    const valid = isTokenValid(getToken());
    if (valid) {
        sessionActive = true;
    }
    return valid;
};

export const isSessionExpired = () => sessionExpired;

/** Токен истёк, но вкладка уже работала в этой сессии: маршрут размонтировать нельзя. */
export const shouldKeepRouteMounted = () => sessionExpired || (sessionActive && Boolean(getToken()));

export const notifySessionExpired = () => {
    if (!sessionActive || sessionExpired) return;
    sessionExpired = true;
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

const markSessionRestored = () => {
    sessionActive = true;
    sessionExpired = false;
    window.dispatchEvent(new CustomEvent(SESSION_RESTORED_EVENT));
};

const readErrorDetail = async (response, fallback) => {
    try {
        const data = await response.json();
        return typeof data?.detail === 'string' && data.detail ? data.detail : fallback;
    } catch {
        return fallback;
    }
};

export const getStoredUser = () => {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const getCurrentUser = async () => {
    const token = getToken();
    if (!token) return null;

    let response;
    try {
        response = await fetchWithTimeout(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch {
        return getStoredUser();
    }

    if (response.status === 401) {
        // Черновики не трогаем: пользователь их не отменял, он просто войдёт заново
        clearSession();
        return null;
    }

    if (await handleAccountDeactivatedResponse(response)) {
        return null;
    }

    if (!response.ok) {
        return getStoredUser();
    }

    try {
        const user = await response.json();
        localStorage.setItem('user', JSON.stringify(user));
        return user;
    } catch {
        return getStoredUser();
    }
};

export const login = async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
    });

    if (!response.ok) {
        throw new Error(await readErrorDetail(response, 'Не удалось войти'));
    }

    let data;
    try {
        data = await response.json();
    } catch {
        throw createNetworkError('Не удалось прочитать ответ сервера. Повторите попытку.');
    }

    storeToken(data.access_token);
    lastRefreshAttemptAt = 0;
    const user = await getCurrentUser();
    markSessionRestored();
    return { ...data, user };
};

export const logout = () => {
    const token = getToken();
    clearSession();
    clearDrafts();

    if (token) {
        // Best effort: сервер сбрасывает cookie загрузок, ошибка не должна мешать выходу
        fetchWithTimeout(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            keepalive: true,
        }).catch(() => {});
    }
};

/** Сообщение для страницы входа; показывается один раз. */
export const peekAccountNotice = () => sessionStorage.getItem(ACCOUNT_NOTICE_KEY) || '';

export const takeAccountNotice = () => {
    const notice = sessionStorage.getItem(ACCOUNT_NOTICE_KEY);
    if (notice) sessionStorage.removeItem(ACCOUNT_NOTICE_KEY);
    return notice || '';
};

/** Деактивированный пользователь не видит сломанных экранов: выход и объяснение на странице входа. */
export const handleAccountDeactivatedResponse = async (response) => {
    if (response?.status !== 403) return false;
    let detail;
    try {
        detail = (await response.clone().json())?.detail;
    } catch {
        return false;
    }
    if (detail?.code !== USER_INACTIVE_CODE) return false;
    sessionStorage.setItem(ACCOUNT_NOTICE_KEY, detail.message || DEFAULT_INACTIVE_MESSAGE);
    logout();
    window.location.assign('/login');
    return true;
};

const needsRefresh = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return false;

    const now = Date.now() / 1000;
    if (payload.exp <= now) return false;

    // В токене сейчас нет iat, поэтому начало жизни запоминаем сами при получении токена
    let issuedAt = Number(payload.iat) || Number(localStorage.getItem(TOKEN_ISSUED_AT_KEY)) || 0;
    if (!issuedAt) {
        issuedAt = Math.floor(now);
        localStorage.setItem(TOKEN_ISSUED_AT_KEY, String(issuedAt));
    }

    const lifetime = payload.exp - issuedAt;
    return lifetime > 0 && (payload.exp - now) < lifetime * REFRESH_WHEN_REMAINING_RATIO;
};

export const refreshToken = () => {
    if (refreshPromise) return refreshPromise;

    const token = getToken();
    if (!isTokenValid(token)) return Promise.resolve(false);

    lastRefreshAttemptAt = Date.now();
    refreshPromise = (async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 401) {
                notifySessionExpired();
                return false;
            }
            if (!response.ok) return false;

            const data = await response.json();
            // Пользователь мог выйти или войти заново, пока шёл запрос
            if (!data?.access_token || getToken() !== token) return false;
            storeToken(data.access_token);
            return true;
        } catch {
            return false;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

export const waitForPendingRefresh = () => refreshPromise || Promise.resolve(false);

/** Фоновое продление: не блокирует запрос, старый токен ещё действует. */
export const refreshTokenIfNeeded = () => {
    const token = getToken();
    if (!token || refreshPromise) return;
    if (Date.now() - lastRefreshAttemptAt < REFRESH_RETRY_COOLDOWN_MS) return;
    if (needsRefresh(token)) {
        refreshToken();
    }
};

const checkSession = () => {
    const token = getToken();
    if (!token) return;
    if (isTokenValid(token)) {
        sessionActive = true;
        refreshTokenIfNeeded();
    } else {
        notifySessionExpired();
    }
};

/**
 * Работает одинаково в браузере и в установленном PWA: фокус окна, возврат на вкладку,
 * возврат сети и таймер для вкладки, которая долго открыта на компьютере без смены фокуса.
 */
export const startSessionKeepAlive = () => {
    const handleVisibility = () => {
        if (document.visibilityState === 'visible') checkSession();
    };

    const handleStorage = (event) => {
        if (event.key !== 'token') return;
        if (!event.newValue) {
            // Выход в другой вкладке браузера
            if (sessionActive) {
                sessionActive = false;
                sessionExpired = false;
                window.location.assign('/login');
            }
        } else if (sessionExpired && isTokenValid(event.newValue)) {
            // Вход выполнен в другой вкладке браузера
            markSessionRestored();
        }
    };

    window.addEventListener('focus', checkSession);
    window.addEventListener('online', checkSession);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibility);
    const intervalId = window.setInterval(checkSession, KEEP_ALIVE_INTERVAL_MS);
    checkSession();

    return () => {
        window.removeEventListener('focus', checkSession);
        window.removeEventListener('online', checkSession);
        window.removeEventListener('storage', handleStorage);
        document.removeEventListener('visibilitychange', handleVisibility);
        window.clearInterval(intervalId);
    };
};
